import { Search } from "@outschool/business-rules";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
import { ChildFragmentFragment } from "@outschool/gql-frontend-generated";
import { uniq } from "lodash";
import { useCallback } from "react";

import useLearnerSpecificSearchV3Experiment from "./useLearnerSpecificSearchV3Experiment";

function mapClassFormatAttributesToExpectedFormat(classFormats: string[]) {
  return classFormats.map(format => {
    switch (format) {
      case "live":
        return "Live online";
      case "self_paced":
        return "Self-Paced";
      default:
        return format;
    }
  });
}

export function mapClassSizeAttributesToExpectedFormat(
  classSizes: string[],
): Search.ClassSize[] {
  return uniq(
    classSizes.flatMap(size => {
      switch (size) {
        case "one_on_one":
          return ["OneOnOne"];
        case "group":
          return ["SmallGroup", "LargeGroup"];
        case "small_group":
          return ["SmallGroup"];
        case "large_group":
          return ["LargeGroup"];
        default:
          return [];
      }
    }) as Search.ClassSize[],
  );
}

function getAttributeValues(
  attributes: { key: string; value: string }[],
  key: string,
) {
  return attributes.filter(attr => attr.key === key).map(attr => attr.value);
}

export function useActiveLearnerSearchFilters(): typeof getSearchFilters {
  const { variant } = useLearnerSpecificSearchV3Experiment();

  const getSearchFilters = useCallback(
    (activeLearner: ChildFragmentFragment | null): SearchFiltersType => {
      if (!activeLearner) {
        return {
          age: null,
          theme: null,
          format: null,
          capacityMin: null,
          capacityMax: null,
          startAfterTime: null,
          endByTime: null,
          dow: null,
        };
      }

      // Control group is handled by the fact that activeLearner is always null in the control group.

      // For treatment group, only apply age filter
      // Other preferences (class size, format) are used as ranking signals only
      if (variant === "treatment") {
        return {
          ...(activeLearner.age && { age: activeLearner.age.toString() }),
        };
      }

      // Fallback (holdout): apply all filters
      return {
        ...(activeLearner.age && { age: activeLearner.age.toString() }),
        ...Search.getCapacityFiltersFromClassSize(
          mapClassSizeAttributesToExpectedFormat(
            getAttributeValues(activeLearner.attributes, "class_size"),
          ),
        ),
        format: mapClassFormatAttributesToExpectedFormat(
          getAttributeValues(activeLearner.attributes, "class_format"),
        ).join(","),
      };
    },
    [variant],
  );

  return getSearchFilters;
}
